import { findIncapacitatedReports } from '@src/main/modules/reports/incapacitated/application/find-incapacitated-reports.usecase';
import {
  IncapacitatedReport,
  IncapacitatedReportCriteria,
} from '@src/main/modules/reports/incapacitated/domain/incapacitated-report.interface';
import { createIncapacitatedReportHttpRepository } from '@src/main/modules/reports/incapacitated/infrastructure/IncapacitatedReportHttpRepository';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';
import { useQuery } from '@tanstack/react-query';
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useMemo, useState } from 'react';

export const useIncapacitatedReports = (criteria: IncapacitatedReportCriteria) => {
  const { user } = useAuth();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('flightDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);
  const repository = useMemo(
    () => (user?.token ? createIncapacitatedReportHttpRepository(tokenProvider) : null),
    [tokenProvider, user?.token],
  );

  const request = useMemo(
    () => ({
      ...criteria,
      offset: pageNumber * pageSize,
      limit: pageSize,
      sortField,
      sortOrder,
    }),
    [criteria, pageNumber, pageSize, sortField, sortOrder],
  );

  const query = useQuery<PageResponse<IncapacitatedReport>>({
    queryKey: ['incapacitatedReports', request],
    queryFn: () => {
      if (!repository) throw new Error('Repository not available');
      return findIncapacitatedReports(repository, request);
    },
    enabled: !!repository,
    placeholderData: (previous) => previous,
  });

  const handlePageChange = useCallback((event: DataTablePageEvent) => {
    setPageNumber(Math.floor(event.first / event.rows));
    setPageSize(event.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'flightDate');
    setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc');
    setPageNumber(0);
  }, []);

  return {
    reports: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    pageNumber,
    pageSize,
    sortField,
    sortOrder,
    setPageNumber,
    handlePageChange,
    handleSort,
  };
};
